import { Node } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import DOMPurify from "dompurify";
import { createContext, memo, useContext, useLayoutEffect, useRef, useState } from "react";

import { Button } from "../button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../collapsible";
import { Icon } from "../icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip";

// A shadow root keeps the signature's own styles from leaking into the editor, and the editor's out of it.
export const EditorExtensionDraftEmailSignatureContent = memo(({ signature }: { signature: string }) => {
  const hostRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }

    const shadowRoot = host.shadowRoot ?? host.attachShadow({ mode: "open" });
    shadowRoot.innerHTML = `<div contenteditable="false">${DOMPurify.sanitize(signature)}</div>`;
  }, [signature]);

  return <div ref={hostRef} />;
});
EditorExtensionDraftEmailSignatureContent.displayName = "EditorExtensionDraftEmailSignatureContent";

const DraftEmailSignatureContext = createContext<string | null | undefined>(undefined);

export const DraftEmailSignatureProvider = DraftEmailSignatureContext.Provider;

const EditorExtensionDraftEmailSignatureComponent = () => {
  const signature = useContext(DraftEmailSignatureContext);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <NodeViewWrapper className="editor-extension-draft-email-signature block my-4 [.ProseMirror-selectednode_&]:ring-2 [.ProseMirror-selectednode_&]:ring-primary/50 [.ProseMirror-selectednode_&]:ring-inset selection:bg-transparent! max-w-full">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <Tooltip>
          <CollapsibleTrigger asChild>
            <TooltipTrigger asChild>
              <Button variant="secondary" size="sm" className="rounded-full py-0 px-3 h-5">
                <Icon icon="signature" />
              </Button>
            </TooltipTrigger>
          </CollapsibleTrigger>
          <TooltipContent>{isOpen ? "Hide signature" : "Show signature"}</TooltipContent>
        </Tooltip>
        <CollapsibleContent>
          <div className="pt-3">
            <EditorExtensionDraftEmailSignatureContent signature={signature ?? ""} />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </NodeViewWrapper>
  );
};

export const EditorExtensionDraftEmailSignature = Node.create({
  name: "draftEmailSignature",
  group: "block",
  atom: true,
  marks: "",

  parseHTML() {
    return [{ priority: 100, tag: 'div[data-type="draft-email-signature"]' }];
  },

  renderHTML() {
    return ["div", { "data-type": "draft-email-signature" }];
  },

  addNodeView() {
    return ReactNodeViewRenderer(EditorExtensionDraftEmailSignatureComponent);
  },
});
